import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { inputStyle } from './DashboardUI';
import { Eye, EyeOff, CreditCard, AlertTriangle } from 'lucide-react';
import EmployeeScheduleEditor from '../TimeManagement/EmployeeScheduleEditor';
import AllEmployeesScheduleEditor from '../TimeManagement/AllEmployeesScheduleEditor';

const DashboardModals = ({
  // Edit PIN
  editPinDialog, setEditPinDialog,
  editPinForm, setEditPinForm,
  showPinInEdit, setShowPinInEdit,
  handleUpdatePin,
  // Edit Name
  editNameDialog, setEditNameDialog,
  editNameForm, setEditNameForm,
  handleUpdateName,
  // Delete
  deleteDialog, setDeleteDialog,
  handleDeleteEmployee,
  // Break Settings
  breakSettingsDialog, setBreakSettingsDialog,
  employeeBreakForm, setEmployeeBreakForm,
  handleUpdateBreakSettings,
  // Status
  showStatusModal, setShowStatusModal,
  employeeStatusUpdate, setEmployeeStatusUpdate,
  updateEmployeeStatus,
  // Schedule Editor
  showScheduleEditor, setShowScheduleEditor,
  employeeToEdit,
  showAllScheduleEditor, setShowAllScheduleEditor,
  currentLocation,
  sessionId,
  loadEmployees,
  // Subscription Management
  showSubscriptionManagement, setShowSubscriptionManagement,
  subscriptionStatus,
  upgradeTarget, setUpgradeTarget,
  handleUpgradeSubscription,
  // Selected employee
  selectedEmployee,
}) => (
  <>
    {/* Edit PIN Dialog */}
    <Dialog open={editPinDialog} onOpenChange={setEditPinDialog}>
      <DialogContent className="bg-zinc-900 border border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle>View/Edit PIN</DialogTitle>
          <DialogDescription className="text-zinc-400">
            View or change PIN for {selectedEmployee?.first_name} {selectedEmployee?.last_name}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <label className="block text-sm text-zinc-400 mb-2">Current PIN</label>
          <div className="flex items-center gap-2">
            <Input
              type={showPinInEdit ? "text" : "password"}
              value={editPinForm.pin}
              onChange={(e) => setEditPinForm({...editPinForm, pin: e.target.value})}
              className={inputStyle}
              maxLength={4}
            />
            <Button type="button" variant="ghost" onClick={() => setShowPinInEdit(!showPinInEdit)} className="text-zinc-400 hover:text-white">
              {showPinInEdit ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditPinDialog(false)} className="border-zinc-700">Cancel</Button>
          <Button onClick={handleUpdatePin} className="bg-violet-500/20 text-violet-200 hover:bg-violet-500/30">Update PIN</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Edit Name/Details Dialog */}
    <Dialog open={editNameDialog} onOpenChange={setEditNameDialog}>
      <DialogContent className="bg-zinc-900 border border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle>Edit Employee Details</DialogTitle>
          <DialogDescription className="text-zinc-400">Update information for this employee</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">First Name</label>
            <Input value={editNameForm.first_name} onChange={(e) => setEditNameForm({...editNameForm, first_name: e.target.value})} className={inputStyle} />
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Last Name</label>
            <Input value={editNameForm.last_name} onChange={(e) => setEditNameForm({...editNameForm, last_name: e.target.value})} className={inputStyle} />
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Email (Optional)</label>
            <Input type="email" value={editNameForm.email} onChange={(e) => setEditNameForm({...editNameForm, email: e.target.value})} className={inputStyle} />
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-2">PIN</label>
            <Input value={editNameForm.pin} onChange={(e) => setEditNameForm({...editNameForm, pin: e.target.value})} className={inputStyle} maxLength={4} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditNameDialog(false)} className="border-zinc-700">Cancel</Button>
          <Button onClick={handleUpdateName} className="bg-violet-500/20 text-violet-200 hover:bg-violet-500/30">Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
      <DialogContent className="bg-zinc-900 border border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle>Delete Employee</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Are you sure you want to delete {selectedEmployee?.first_name} {selectedEmployee?.last_name}? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDeleteDialog(false)} className="border-zinc-700">Cancel</Button>
          <Button onClick={handleDeleteEmployee} className="bg-rose-500/20 text-rose-200 hover:bg-rose-500/30">Delete Employee</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Break Settings Dialog */}
    <Dialog open={breakSettingsDialog} onOpenChange={setBreakSettingsDialog}>
      <DialogContent className="bg-zinc-900 border border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle>Break Settings</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Custom break settings for {selectedEmployee?.first_name} {selectedEmployee?.last_name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Break Duration (minutes)</label>
            <Input type="number" value={employeeBreakForm.custom_break_minutes} onChange={(e) => setEmployeeBreakForm({...employeeBreakForm, custom_break_minutes: parseInt(e.target.value)})} className={inputStyle} />
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Work Hours Before Break</label>
            <Input type="number" step="0.5" value={employeeBreakForm.custom_work_hours} onChange={(e) => setEmployeeBreakForm({...employeeBreakForm, custom_work_hours: parseFloat(e.target.value)})} className={inputStyle} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setBreakSettingsDialog(false)} className="border-zinc-700">Cancel</Button>
          <Button onClick={handleUpdateBreakSettings} className="bg-violet-500/20 text-violet-200 hover:bg-violet-500/30">Save Settings</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Status Update Modal */}
    <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
      <DialogContent className="bg-zinc-900 border border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle>Update Employee Status</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Change status for {selectedEmployee?.first_name} {selectedEmployee?.last_name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Status</label>
            <Select value={employeeStatusUpdate.status} onValueChange={(value) => setEmployeeStatusUpdate({...employeeStatusUpdate, status: value})}>
              <SelectTrigger className={inputStyle}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_leave">On Leave</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="terminated">Terminated</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {employeeStatusUpdate.status === 'on_leave' && (
            <>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Leave Start Date</label>
                <Input type="date" value={employeeStatusUpdate.startDate} onChange={(e) => setEmployeeStatusUpdate({...employeeStatusUpdate, startDate: e.target.value})} className={inputStyle} />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Leave End Date</label>
                <Input type="date" value={employeeStatusUpdate.endDate} onChange={(e) => setEmployeeStatusUpdate({...employeeStatusUpdate, endDate: e.target.value})} className={inputStyle} />
              </div>
            </>
          )}
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Note (Optional)</label>
            <Input value={employeeStatusUpdate.note} onChange={(e) => setEmployeeStatusUpdate({...employeeStatusUpdate, note: e.target.value})} placeholder="Add a note..." className={inputStyle} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowStatusModal(false)} className="border-zinc-700">Cancel</Button>
          <Button onClick={updateEmployeeStatus} className="bg-violet-500/20 text-violet-200 hover:bg-violet-500/30">Update Status</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Schedule Editor Modal */}
    {showScheduleEditor && employeeToEdit && (
      <EmployeeScheduleEditor
        employee={employeeToEdit}
        sessionId={sessionId}
        onSaved={() => { setShowScheduleEditor(false); loadEmployees(); }}
        onClose={() => setShowScheduleEditor(false)}
      />
    )}

    {/* All Employees Schedule Editor */}
    {showAllScheduleEditor && currentLocation && (
      <AllEmployeesScheduleEditor
        locationId={currentLocation.id}
        sessionId={sessionId}
        onSaved={() => { setShowAllScheduleEditor(false); loadEmployees(); }}
        onClose={() => setShowAllScheduleEditor(false)}
      />
    )}

    {/* Subscription Management Dialog */}
    <Dialog open={showSubscriptionManagement} onOpenChange={setShowSubscriptionManagement}>
      <DialogContent className="bg-zinc-900 border border-zinc-800 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-violet-400" />
            Subscription Management
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {subscriptionStatus?.is_trial
              ? `Your trial ends in ${subscriptionStatus.trial_days_remaining || 0} days`
              : `Current plan: ${(subscriptionStatus?.plan || 'starter').charAt(0).toUpperCase() + (subscriptionStatus?.plan || 'starter').slice(1)}`
            }
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-3">
            {/* Starter Plan */}
            <button
              onClick={() => subscriptionStatus?.plan !== 'starter' && setUpgradeTarget('starter')}
              disabled={subscriptionStatus?.plan === 'starter'}
              className={`w-full p-4 rounded-xl border text-left transition ${
                subscriptionStatus?.plan === 'starter' 
                  ? 'border-emerald-500 bg-emerald-500/20 cursor-default' 
                  : upgradeTarget === 'starter' 
                    ? 'border-emerald-500 bg-emerald-500/10' 
                    : 'border-zinc-700 hover:border-zinc-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-white">Starter Plan</div>
                {subscriptionStatus?.plan === 'starter' && (
                  <span className="text-xs bg-emerald-500/30 text-emerald-300 px-2 py-0.5 rounded-full">Current</span>
                )}
              </div>
              <div className="text-sm text-zinc-400">£49/month - Up to 15 staff, live dashboard, hours reporting</div>
            </button>
            
            {/* Growth Plan */}
            <button
              onClick={() => subscriptionStatus?.plan !== 'growth' && setUpgradeTarget('growth')}
              disabled={subscriptionStatus?.plan === 'growth'}
              className={`w-full p-4 rounded-xl border text-left transition ${
                subscriptionStatus?.plan === 'growth' 
                  ? 'border-violet-500 bg-violet-500/20 cursor-default' 
                  : upgradeTarget === 'growth' 
                    ? 'border-violet-500 bg-violet-500/10' 
                    : 'border-zinc-700 hover:border-zinc-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-white">Growth Plan</div>
                {subscriptionStatus?.plan === 'growth' && (
                  <span className="text-xs bg-violet-500/30 text-violet-300 px-2 py-0.5 rounded-full">Current</span>
                )}
              </div>
              <div className="text-sm text-zinc-400">£69/month - Up to 30 staff, everything in Starter + Rota builder</div>
            </button>
            
            {/* Pro Plan */}
            <button
              onClick={() => subscriptionStatus?.plan !== 'pro' && setUpgradeTarget('pro')}
              disabled={subscriptionStatus?.plan === 'pro'}
              className={`w-full p-4 rounded-xl border text-left transition ${
                subscriptionStatus?.plan === 'pro' 
                  ? 'border-amber-500 bg-amber-500/20 cursor-default' 
                  : upgradeTarget === 'pro' 
                    ? 'border-amber-500 bg-amber-500/10' 
                    : 'border-zinc-700 hover:border-zinc-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-white">Pro Plan</div>
                {subscriptionStatus?.plan === 'pro' && (
                  <span className="text-xs bg-amber-500/30 text-amber-300 px-2 py-0.5 rounded-full">Current</span>
                )}
              </div>
              <div className="text-sm text-zinc-400">£99/month - Up to 60 staff, everything in Growth + advanced rules</div>
            </button>
          </div>
          
          {upgradeTarget && upgradeTarget !== subscriptionStatus?.plan && (
            <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
              <p className="text-sm text-zinc-300">
                {['growth', 'pro', 'enterprise'].indexOf(upgradeTarget) > ['growth', 'pro', 'enterprise'].indexOf(subscriptionStatus?.plan || 'starter')
                  ? '⬆️ Upgrading will give you immediate access to new features. Your old subscription will be cancelled.'
                  : '⬇️ Downgrading will take effect at the end of your current billing period.'
                }
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowSubscriptionManagement(false)} className="border-zinc-700">Cancel</Button>
          <Button
            onClick={() => handleUpgradeSubscription(upgradeTarget)}
            disabled={!upgradeTarget || upgradeTarget === subscriptionStatus?.plan}
            className="bg-gradient-to-r from-violet-500/30 to-emerald-500/30 text-white hover:from-violet-500/40 hover:to-emerald-500/40 disabled:opacity-50"
          >
            {upgradeTarget && upgradeTarget !== subscriptionStatus?.plan 
              ? `${['growth', 'pro', 'enterprise'].indexOf(upgradeTarget) > ['growth', 'pro', 'enterprise'].indexOf(subscriptionStatus?.plan || 'starter') ? 'Upgrade' : 'Downgrade'} to ${upgradeTarget.charAt(0).toUpperCase() + upgradeTarget.slice(1)}` 
              : 'Select a plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
);

export default DashboardModals;
